import { CommonModule } from "@angular/common";
import {
  Component,
  Inject,
  inject,
  TemplateRef,
  ViewChild,
  OnInit,
  OnDestroy,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { ServiceCancelData } from "@features/service-actions/models/service-action.model";
import { ServiceCancellationRequest } from "@features/service-actions/models/service-cancel.model";
import { ServiceCancelState } from "@features/service-actions/store/service-cancel.state";
import { SERVICE_CANCEL_CHANNELS, SERVICE_CANCEL_META } from "@features/service-actions/constants/service-cancel.constants";
import { HorusLoadingComponent } from "@shared/components/domain/horus-loading/horus-loading.component";
import { HorusMessageGeneralComponent } from "@shared/components/domain/horus-message-general/horus-message-general.component";
import { ConfirmV2DialogComponent } from "@shared/components/ui/confirm-v2-dialog/confirm-v2-dialog.component";
import { DialogManagerService } from "app/shared/services/dialog-manager.service";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { getAriaTicketUrl } from "@core/utils/url";
import { HorusBtnDirective } from "@shared/directives/horus-btn.directive";

@Component({
  selector: "app-service-cancel-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    HorusLoadingComponent,
    HorusMessageGeneralComponent,
    HorusBtnDirective,
  ],
  templateUrl: "./service-cancel-dialog.component.html",
  styleUrl: "./service-cancel-dialog.component.scss",
})
export class ServiceCancelDialogComponent implements OnInit, OnDestroy {
  @ViewChild("confirmTemplate") confirmTemplate!: TemplateRef<any>;

  dialogRef = inject(MatDialogRef<ServiceCancelDialogComponent>);
  dialogManagerService = inject(DialogManagerService);
  dialog = inject(MatDialog);
  serviceCancelState = inject(ServiceCancelState);
  fb = inject(FormBuilder);
  serviceCancelForm: FormGroup;
  tenantUrlService = inject(TenantUrlUtilService);

   constructor(@Inject(MAT_DIALOG_DATA) public data: ServiceCancelData) {
    this.serviceCancelForm = this.fb.group({
      observations: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.serviceCancelState.serviceId.set(this.data.serviceId);
    this.serviceCancelState.childId.set(this.data.serviceItemId);
  }

  onOpenConfirmServiceCancellation() {
    this.serviceCancelState.resetMutationState();

    const dialogRef = this.dialog.open(ConfirmV2DialogComponent, {
      height: "450px",
      width: "427px",
      panelClass: "horus-confirmation-panel",
      data: {
        contentTemplate: this.confirmTemplate,
        close: () => {
          this.serviceCancelState.resetMutationState();
          dialogRef.close();
        },
        closeAll: () => {
          this.serviceCancelState.resetMutationState();
          this.dialog.closeAll();
        },
      },
      disableClose: false,
      restoreFocus: false,
    });
  }

  onConfirmServiceCancellation() {
    if (this.serviceCancelForm.valid) {
      const request: ServiceCancellationRequest = {
        tenant: this.data.tenantId.toUpperCase(),
        osId: this.data.serviceId,
        orderId: this.data.orderId,
        type: SERVICE_CANCEL_META.type,
        user: this.tenantUrlService.getAgent(),
        seller: this.data.seller,
        channel: SERVICE_CANCEL_CHANNELS.HORUS,
        childId: this.data.serviceItemId,
        observations: this.serviceCancelForm.value.observations
      };

      const tenantId = this.data.tenantId.toLowerCase();
      this.serviceCancelState.cancelServiceMutation.mutate({
        tenantId,
        request,
      });
    }
  }

  closeAllDialogs() {
    this.serviceCancelState.resetMutationState();
    this.dialog.closeAll();
  }

  redirectToAria(): void {
    const tenantId = this.data.tenantId?.toUpperCase();
    if (tenantId && this.data.serviceId) {
      const url = getAriaTicketUrl(tenantId, this.data.serviceId, "SERVICE");
      window.open(url, "_blank");
    }
  }

  ngOnDestroy(): void {
    this.serviceCancelState.resetMutationState();
  }
}
